import { useSearchParams } from 'react-router-dom';
import { C } from '../../lib/tokens.js';
import { t } from '../../lib/data.js';
import { useSystems, useSystemTimeline } from '../../lib/useSystems.js';
import { useLookups } from '../../lib/LookupsContext.jsx';
import { PageShell } from '../../components/layout/PageShell.jsx';
import { SearchableSelect } from '../../components/ui/Fields.jsx';
import { PageSpinner } from '../../components/ui/Misc.jsx';
import { SystemTimeline } from './SystemTimeline.jsx';

export function AssetsPage({ role }) {
  const { systems, loading: systemsLoading } = useSystems();
  const { bpartners } = useLookups();
  // URL-backed (see RequestClusterPage) so a shared link / back button restores the filters.
  const [searchParams, setSearchParams] = useSearchParams();
  const systemId = searchParams.get('system');
  const bpartnerFilter = searchParams.get('bp') || '';

  const { entries, loading: timelineLoading } = useSystemTimeline(systemId);

  // Agents see every customer's systems, so copy can't claim "my" ownership.
  const isAgent = role === 'agent';

  // Only agents span multiple customers, so only they get this filter.
  const visibleSystems = isAgent && bpartnerFilter
    ? systems.filter(s => s.bpartnerId === bpartnerFilter)
    : systems;

  const bpartnerOptions = bpartners.map(b => ({ value: b.id, label: b.name }));

  function setSystemId(id) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (id) next.set('system', id); else next.delete('system');
      return next;
    }, { replace: true });
  }

  // Clears the system selection too if the new bp filter would hide it -
  // both params update in one call so there's no in-between invalid state.
  function handleBpartnerFilterChange(value) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set('bp', value); else next.delete('bp');
      const curId = prev.get('system');
      if (curId && value && !systems.some(s => s.id === curId && s.bpartnerId === value)) {
        next.delete('system');
      }
      return next;
    }, { replace: true });
  }

  // Agent options are labeled with customer name and sorted by it, since
  // same-named systems across customers would otherwise be indistinguishable.
  const systemOptions = isAgent
    ? [...visibleSystems]
        .sort((a, b) => (a.customerName || '').localeCompare(b.customerName || '') || a.name.localeCompare(b.name))
        .map(s => ({ value: s.id, label: s.customerName ? `${s.customerName} — ${s.name}` : s.name }))
    : visibleSystems.map(s => ({ value: s.id, label: s.name }));

  return (
    <PageShell
      title={isAgent ? t('assets.title_agent') : t('assets.title')}
      subtitle={isAgent ? t('assets.subtitle_agent') : t('assets.subtitle')}
    >
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        {isAgent && (
          <div style={{ maxWidth: 280, flex: '1 1 240px' }}>
            <SearchableSelect
              label="Business partner"
              value={bpartnerFilter}
              onChange={handleBpartnerFilterChange}
              options={bpartnerOptions}
              placeholder="All business partners"
            />
          </div>
        )}
        <div style={{ maxWidth: 320, flex: '1 1 240px' }}>
          <SearchableSelect
            label="System"
            value={systemId}
            onChange={setSystemId}
            options={systemOptions}
            placeholder={systemsLoading ? 'Loading…' : 'Select a system…'}
          />
        </div>
      </div>

      {systemsLoading ? (
        <PageSpinner label="Loading systems…" />
      ) : !systemId ? (
        <div style={{ fontSize: 13, color: C.silver }}>
          {systems.length === 0
            ? 'No systems found.'
            : visibleSystems.length === 0
              ? 'No systems found for this business partner.'
              : 'Select a system to view its update history.'}
        </div>
      ) : (
        <div style={{ background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6, padding: '18px 20px 6px' }}>
          <SystemTimeline entries={entries} loading={timelineLoading} />
        </div>
      )}
    </PageShell>
  );
}
